<script setup lang="ts">
import { useNuxtApp } from '@nuxt/bridge/dist/runtime'
import { useConsumableStore } from '@/stores/useConsumableStore'
import { useAddressStore } from '@/stores/useAddressStore'
import { onBeforeMount, computed, ref } from 'vue'
import constants from '@/assets/constants.json'

// Nuxt
const nuxtApp = useNuxtApp()
const { $env }: any = nuxtApp

// Props
const props = defineProps({
  mode: {
    type: String,
    required: true,
  },
  redirectPath: {
    type: String,
    required: false,
    default: '',
  },
  hasActionButton: {
    type: Boolean,
    required: false,
    default: false,
  },
})

// OnBeforeMount
onBeforeMount(async () => {
  try {
    await Promise.all([
      useAddressStore().fetchPpdInfo(),
      useAddressStore().fetchBpcBalance(),
      useAddressStore().fetchElsBalance(),
    ])
  } catch (e) {
    console.error(e)
  }
})

// Computed
const getBalance = computed(() => {
  switch (props.mode) {
    case 'ppd':
      return useAddressStore().getPpdBalance
    case 'landVolume':
      return useConsumableStore().getMyBalance.landVolume
    case 'mem':
      return useConsumableStore().getMyBalance.mem
    case 'memoriaTicket':
      return useConsumableStore().getMyBalance.memoriaTicket
    case 'specialMemoriaTicket':
      return useConsumableStore().getMyBalance.specialMemoriaTicket
    case 'elsPoint':
      return useConsumableStore().getMyBalance.elsPoint
    case 'bpcPoint':
      return useConsumableStore().getMyBalance.bpcPoint
    case 'mintTicket':
      return useConsumableStore().getMyBalance.mintTicket
    case 'uMintTicket':
      return useConsumableStore().getMyBalance.uMintTicket
    case 'gachaCoin':
      return useConsumableStore().getMyBalance.gachaCoin
    case 'frontierPoint':
      return useConsumableStore().getMyBalance.frontierPoint
    case 'bpc':
      return useAddressStore().getBpcBalance
    case 'els':
      return useAddressStore().getElsBalance
    default:
      return 0
  }
})

const getImageUrl = computed(() => {
  switch (props.mode) {
    case 'ppd':
      return `${$env.staticUrl}/gem/gemIconThin.png`
    case 'landVolume':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.LandVolume)?.imageUrl
    case 'mem':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.Mem)?.imageUrl
    case 'memoriaTicket':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.MemoriaTicket)?.imageUrl
    case 'specialMemoriaTicket':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.SpecialMemoriaTicket)?.imageUrl
    case 'elsPoint':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.ElsPoint)?.imageUrl
    case 'bpcPoint':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.BpcPoint)?.imageUrl
    case 'mintTicket':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.MintTicket)?.imageUrl
    case 'uMintTicket':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.UMintTicket)?.imageUrl
    case 'gachaCoin':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.GachaCoin)?.imageUrl
    case 'frontierPoint':
      return useConsumableStore().getConsumeItem(constants.EXCHANGE_CONSUMEIDS.FrontierPoint)?.imageUrl
    case 'els':
      return '/images/i_icon_els.png'
    case 'bpc':
      return '/images/i_icon_bpc.png'
    default:
      return ''
  }
})

// Action
const emit = defineEmits(['action'])

const action = () => {
  emit('action')
}
</script>

<template>
  <div
    class="flex w-60 justify-between p-1 border border-solid border-bfh-border bg-bfh-main-dark-sub text-bfh-text-light"
  >
    <img :src="getImageUrl" class="h-6 self-center" />
    <span class="ml-auto pr-4">{{ getBalance }}</span>
    <nuxt-link v-if="redirectPath" :to="redirectPath">
      <fa-icon :icon="['fas', 'plus-circle']" style="color:#FFA800" size="lg" />
    </nuxt-link>
    <button v-if="hasActionButton" @click="action">
      <fa-icon :icon="['fas', 'plus-circle']" style="color:#FFA800" size="lg" />
    </button>
  </div>
</template>
